import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@ibk/shared-translation';
import { Icon, Input, Loading } from '@ibk/shared-ui';
import { ContentMessage } from '../../../components/ContentMessage';
import { SlowRequestFeedback } from '../../../components/SlowRequestFeedback';
import { useGetInstructions } from '../../../hooks/useGetInstructions';
import { RequestTimeoutState } from '../../../hooks/useRequestTimeout';
import styles from '../Instructions.module.css';
export function InstructionsTableBase({ status, approvalStatus, dataTestId, noContentTitleKey = 'instructions-page.info.pending.no-content.title', noContentSubtitleKey = 'instructions-page.info.pending.no-content.subtitle', children, }) {
    const { t, currentLanguage } = useTranslation();
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const loadMoreTriggerRef = useRef(null);
    const { instructionsData, hasNoData, loadingMore, initialLoading, hasMoreData, getNextPage, requestState, retryFetch, } = useGetInstructions(status, approvalStatus, search);
    useEffect(() => {
        const triggerElement = loadMoreTriggerRef.current;
        if (!triggerElement)
            return;
        const observer = new IntersectionObserver(entries => {
            const [entry] = entries;
            if (entry.isIntersecting && hasMoreData && !loadingMore && instructionsData && instructionsData.length > 0) {
                getNextPage();
            }
        }, {
            threshold: 0.1,
            rootMargin: '100px',
        });
        observer.observe(triggerElement);
        return () => {
            observer.unobserve(triggerElement);
            observer.disconnect();
        };
    }, [hasMoreData, loadingMore, getNextPage, instructionsData]);
    useEffect(() => {
        return () => {
            setSearch('');
        };
    }, []);
    const handleTableContent = () => {
        const shouldShowRequestFeedback = (requestState === RequestTimeoutState.Slow ||
            requestState === RequestTimeoutState.Timeout ||
            requestState === RequestTimeoutState.Error) &&
            (!instructionsData || instructionsData.length === 0);
        if (shouldShowRequestFeedback && requestState) {
            return (_jsx(SlowRequestFeedback, { dataTestId: `${dataTestId}-request`, state: requestState, onRetry: retryFetch, onGoBack: () => navigate('/home') }));
        }
        if (initialLoading)
            return (_jsx("div", { className: styles.loadingContainer, children: _jsx(Loading, { "data-testid": 'instructions-loading' }) }));
        if (hasNoData && search.length >= 3)
            return (_jsx(ContentMessage, { icon: 'bigBlockCircle', dataTestId: 'instructions-page.info.no-content-search', title: t('instructions-page.info.no-content-search.title'), subtitle: t('instructions-page.info.no-content-search.subtitle') }));
        if (hasNoData)
            return (_jsx(ContentMessage, { icon: 'bigInformationCircle', dataTestId: 'instructions-page.info.pending.no-content', title: t(noContentTitleKey), subtitle: t(noContentSubtitleKey) }));
        const tableData = {
            instructionsData,
            hasNoData,
            loadingMore,
            initialLoading,
            hasMoreData,
            getNextPage,
            currentLanguage,
            t,
        };
        return (_jsxs(_Fragment, { children: [children(tableData), !loadingMore && instructionsData && instructionsData.length > 0 && (_jsx("div", { ref: loadMoreTriggerRef, className: styles.loadMoreTrigger, "data-testid": 'load-more-trigger' })), loadingMore && (_jsx("div", { className: styles.loadingMoreContainer, children: _jsx(Loading, { "data-testid": 'instructions-loading', size: 'micro' }) }))] }));
    };
    return (_jsxs("div", { className: styles.mainTable, children: [_jsx(Input, { leftElement: _jsx(Icon, { name: 'search', className: styles.searchIcon }), containerClassName: styles.searchInput, placeholder: t('instructions-page.search-bar.placeholder'), dataTestId: dataTestId, onChange: e => setSearch(e.target.value), value: search, name: 'search', size: 'small' }), _jsx("div", { className: styles.content, children: handleTableContent() })] }));
}
